import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BarChart, TrendingUp, Calendar, Filter, ArrowLeft, Download, Trash2, Database, AlertTriangle, Package, Settings as SettingsIcon } from 'lucide-react';
import Layout from './Layout';
import { useStore } from '../context/StoreContext';
import { useAlert } from '../context/AlertContext';
import './Dashboard.css';
import './Management.css';

const Analytics = () => {
    const navigate = useNavigate();
    const { stores, user, currentDate, clearVendorsData, clearSalesData, clearInventoryData, clearFacilityData, clearOrderData, clearAllData, backups, createBackup, restoreFromBackup, deleteBackup, admins } = useStore();
    const { showAlert } = useAlert();
    const location = useLocation();
    const [viewType, setViewType] = useState('monthly'); // 'daily', 'monthly', 'quarterly', 'yearly'
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedStore, setSelectedStore] = useState('all');
    const [filterType, setFilterType] = useState('all'); // 'all', 'direct', 'rental'
    const [serverBackups, setServerBackups] = useState([]);

    // 서버 사이드 백업 목록 가져오기
    useEffect(() => {
        const fetchServerBackups = async () => {
            try {
                // 환경별 분기(localhost vs production)는 vite proxy와 nginx proxy가 각각 해결해주므로 상대경로 호출을 사용합니다.
                const res = await fetch('/api/backups_files');
                if (res.ok) {
                    const data = await res.json();
                    setServerBackups(data);
                }
            } catch (err) {
                console.error('Failed to fetch server backups:', err);
            }
        };
        fetchServerBackups();
    }, []);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const queryDate = queryParams.get('date');

        if (queryDate) {
            const [y, m] = queryDate.split('-');
            if (selectedYear !== y) setSelectedYear(y);
            if (selectedMonth !== m) setSelectedMonth(m);
            if (queryDate.split('-').length > 2) setViewType('daily');
        } else if (currentDate && !selectedYear && !selectedMonth) {
            // URL 파라미터가 없고, 아직 연/월이 설정되지 않은 초기 진입 시에만 오늘 날짜로 설정
            const [y, m] = currentDate.split('-');
            setSelectedYear(y);
            setSelectedMonth(m);
        }
    }, [location.search, currentDate]);
    
    // 권한 체크 useEffect로 이동
    useEffect(() => {
        if (user && user.role !== 'admin') {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    const years = ['2024', '2025', '2026'];
    const months = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

    // 데이터 집계 로직
    const { chartData, topStores, prevTotalSum } = useMemo(() => {
        const stats = {}; 
        const prevStats = {}; // 이전 기간 비교용
        const storeStats = {}; // 매장별 순위용
        
        let filteredStores = stores;
        
        // 1. 전체 매장 조회 시 운영 형태 필터링 적용
        if (selectedStore === 'all') {
            if (filterType === 'direct') {
                filteredStores = stores.filter(s => s.operatingType === 'direct');
            } else if (filterType === 'rental') {
                filteredStores = stores.filter(s => s.operatingType === 'rental');
            }
        } else {
            filteredStores = stores.filter(s => s.id === selectedStore);
        }

        // 이전 기간 기준 설정
        let prevYear = selectedYear;
        let prevMonth = selectedMonth;
        if (viewType === 'monthly') {
            prevYear = String(Number(selectedYear) - 1);
        } else if (viewType === 'daily') {
            const m = parseInt(selectedMonth);
            if (m === 1) {
                prevMonth = '12';
                prevYear = String(Number(selectedYear) - 1);
            } else {
                prevMonth = String(m - 1).padStart(2, '0');
            }
        }


        // 2. 통합 데이터 생성 및 '연간 통합 첫 매출일' 식별
        filteredStores.forEach(store => {
            const uniqueRecordsMap = new Map();
            (store.salesData || []).forEach(item => {
                if (!item) return;
                const d = item.localDate || (item.date ? item.date.split('T')[0] : '');
                if (d) uniqueRecordsMap.set(d, item);
            });
            Object.entries(store.salesHistory || {}).forEach(([dateKey, record]) => {
                const innerD = dateKey || record.localDate || (record.date ? record.date.split('T')[0] : null);
                if (innerD) uniqueRecordsMap.set(innerD, record);
            });

            uniqueRecordsMap.forEach((record, d) => {
                const dateStr = d;
                const [year, month, day] = dateStr.split('-');
                const total = Number(record.total) || (Number(record.cash || 0) + Number(record.card || 0));

                if (viewType === 'daily' && year === selectedYear && month === selectedMonth) {
                    stats[day] = (stats[day] || 0) + total;
                    storeStats[store.name] = (storeStats[store.name] || 0) + total;
                } else if (viewType === 'monthly' && year === selectedYear) {
                    stats[month] = (stats[month] || 0) + total;
                    storeStats[store.name] = (storeStats[store.name] || 0) + total;
                } else if (viewType === 'quarterly' && year === selectedYear) {
                    const m = parseInt(month);
                    const quarter = `${Math.floor((m - 1) / 3) + 1}분기`;
                    stats[quarter] = (stats[quarter] || 0) + total;
                } else if (viewType === 'yearly') {
                    stats[year] = (stats[year] || 0) + total;
                }

                // 이전 기간 데이터 (비교용)
                if (viewType === 'daily' && year === prevYear && month === prevMonth) {
                    prevStats[day] = (prevStats[day] || 0) + total;
                } else if (viewType === 'monthly' && year === prevYear) {
                    prevStats[month] = (prevStats[month] || 0) + total;
                }
            });
        });

        // '연간 전사 통합 첫 매출 전월' 확인
        const previousMonthsInYear = Array.from({ length: parseInt(selectedMonth) - 1 }, (_, i) => 
            String(i + 1).padStart(2, '0')
        );
        
        const hasSalesInPreviousMonths = filteredStores.some(store => {
            const history = store.salesHistory || {};
            const data = store.salesData || [];
            const hasHistory = previousMonthsInYear.some(m => {
                const monthPrefix = `${selectedYear}-${m}`;
                return Object.keys(history).some(date => date.startsWith(monthPrefix));
            });
            const hasData = previousMonthsInYear.some(m => {
                const monthPrefix = `${selectedYear}-${m}`;
                return data.some(item => (item.localDate || item.date?.split('T')[0] || '').startsWith(monthPrefix));
            });
            return hasHistory || hasData;
        });

        // 차트 데이터 구성
        let finalChartData = [];
        if (viewType === 'daily') {
            const daysInMonth = new Date(Number(selectedYear), Number(selectedMonth), 0).getDate();
            let hasFoundFirstSalesThisYear = false;

            if (hasSalesInPreviousMonths) hasFoundFirstSalesThisYear = true;

            finalChartData = Array.from({ length: daysInMonth }, (_, i) => {
                const d = (i + 1).toString().padStart(2, '0');
                const value = stats[d] || 0;
                return { label: `${i + 1}일`, value, day: d };
            });

            // 순차적 증감 계산 (과거의 가장 최근 유효 매출과 비교)
            finalChartData.forEach((item, index) => {
                let growth = 0;
                let isNew = false;
                let validPrevVal = 0;
                
                if (item.value > 0 && !hasFoundFirstSalesThisYear) {
                    isNew = true;
                    hasFoundFirstSalesThisYear = true;
                }

                if (item.value > 0 && index > 0) {
                    // 가장 최근의 0초과 매출액 찾기
                    for (let j = index - 1; j >= 0; j--) {
                        if (finalChartData[j].value > 0) {
                            validPrevVal = finalChartData[j].value;
                            break;
                        }
                    }
                    if (validPrevVal > 0) {
                        growth = ((item.value - validPrevVal) / validPrevVal) * 100;
                    } else {
                        growth = 0;
                    }
                } else {
                    growth = 0; 
                }

                // 신규 첫 거래일인 경우 증감율은 0 또는 큰 의미가 없으므로 0으로 세팅하여 가독성 확보
                if (isNew) growth = 0;
                
                item.growth = growth;
                item.isNew = isNew;
                item.prevVal = validPrevVal;
            });
        } else if (viewType === 'monthly') {
            let hasFoundFirstSalesThisYear = false;
            finalChartData = months.map(m => ({
                label: `${Number(m)}월`,
                value: stats[m] || 0,
                month: m
            }));

            finalChartData.forEach((item, index) => {
                let growth = 0;
                let isNew = false;
                let validPrevVal = 0;

                if (item.value > 0 && !hasFoundFirstSalesThisYear) {
                    isNew = true;
                    hasFoundFirstSalesThisYear = true;
                }

                if (item.value > 0 && index > 0) {
                    for (let j = index - 1; j >= 0; j--) {
                        if (finalChartData[j].value > 0) {
                            validPrevVal = finalChartData[j].value;
                            break;
                        }
                    }
                    if (validPrevVal > 0) {
                        growth = ((item.value - validPrevVal) / validPrevVal) * 100;
                    } else {
                        growth = 0;
                    }
                }

                if (isNew) growth = 0;

                item.growth = growth;
                item.isNew = isNew;
                item.prevVal = validPrevVal;
            });
        } else {
            // 분기별/연별도 순차 비교 적용
            finalChartData = Object.entries(stats)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([label, value]) => ({ 
                    label: label, 
                    value 
                }));

            finalChartData.forEach((item, index) => {
                let validPrevVal = 0;
                if (item.value > 0 && index > 0) {
                    for (let j = index - 1; j >= 0; j--) {
                        if (finalChartData[j].value > 0) {
                            validPrevVal = finalChartData[j].value;
                            break;
                        }
                    }
                }
                
                item.growth = validPrevVal > 0 ? ((item.value - validPrevVal) / validPrevVal) * 100 : 0;
                item.prevVal = validPrevVal;
                item.isNew = index === 0 && item.value > 0;
            });
        }

        // TOP 3 매장 추출
        const topStores = Object.entries(storeStats)
            .map(([name, total]) => ({ name, total }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 3);

        const currentTotal = finalChartData.reduce((acc, curr) => acc + curr.value, 0);
        const prevTotal = Object.values(prevStats).reduce((acc, curr) => acc + curr, 0);

        return { chartData: finalChartData, topStores, prevTotalSum: prevTotal };
    }, [stores, viewType, selectedYear, selectedMonth, selectedStore, filterType]);

    const maxValue = Math.max(...chartData.map(d => d.value), 1);

    // 요약 정보 계산
    const totalSum = useMemo(() => chartData.reduce((acc, curr) => acc + curr.value, 0), [chartData]);
    const averageSales = useMemo(() => chartData.length > 0 ? totalSum / chartData.length : 0, [totalSum, chartData]);
    const totalGrowth = useMemo(() => {
        const rawGrowth = ((totalSum - prevTotalSum) / (prevTotalSum || 1)) * 100;
        if (prevTotalSum === 0 && totalSum > 0) {
            // 합계에서는 '신규' 대신 999%+(또는 계산된 값)를 보여달라는 요청 반영
            return '999%+'; 
        }
        return rawGrowth > 999 ? '999%+' : (rawGrowth < -999 ? '-999%+' : `${rawGrowth.toFixed(1)}%`);
    }, [totalSum, prevTotalSum]);

    // 화면 표시용 필터 이름
    const filterLabel = selectedStore !== 'all' ? stores.find(s => s.id === selectedStore)?.name : (filterType === 'all' ? '전체' : (filterType === 'direct' ? '직영' : '임대'));


    if (user?.role !== 'admin') {
        return null;
    }

    return (
        <Layout>
            <div style={{ padding: '32px', maxWidth: '100%', margin: '0 auto' }}>
                <div className="content-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
                        <button onClick={() => navigate('/dashboard')} className="icon-button" title="뒤로가기">
                            <ArrowLeft size={20} />
                        </button>
                        <h1>매출 추이 분석</h1>
                    </div>
                    <p style={{ color: '#94a3b8' }}>전체 매장의 데이터를 분석하여 경영 전략을 수립하세요.</p>
                </div>

                <div className="glass-card" style={{ marginBottom: '24px', padding: '24px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                            <div className="tab-group" style={{ display: 'flex', backgroundColor: 'rgba(255, 255, 255, 0.05)', padding: '4px', borderRadius: '8px' }}>
                                <button
                                    className={`tab-button ${viewType === 'daily' ? 'active' : ''}`}
                                    onClick={() => setViewType('daily')}
                                    style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', background: viewType === 'daily' ? '#38bdf8' : 'transparent', color: viewType === 'daily' ? '#fff' : '#94a3b8' }}
                                >일별</button>
                                <button
                                    className={`tab-button ${viewType === 'monthly' ? 'active' : ''}`}
                                    onClick={() => setViewType('monthly')}
                                    style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', background: viewType === 'monthly' ? '#38bdf8' : 'transparent', color: viewType === 'monthly' ? '#fff' : '#94a3b8' }}
                                >월별</button>
                                <button
                                    className={`tab-button ${viewType === 'quarterly' ? 'active' : ''}`}
                                    onClick={() => setViewType('quarterly')}
                                    style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', background: viewType === 'quarterly' ? '#38bdf8' : 'transparent', color: viewType === 'quarterly' ? '#fff' : '#94a3b8' }}
                                >분기별</button>
                                <button
                                    className={`tab-button ${viewType === 'yearly' ? 'active' : ''}`}
                                    onClick={() => setViewType('yearly')}
                                    style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', background: viewType === 'yearly' ? '#38bdf8' : 'transparent', color: viewType === 'yearly' ? '#fff' : '#94a3b8' }}
                                >연별</button>
                            </div>

                            {/* 운영 형태 필터 (전체 매장 조회 시에만 노출) */}
                            {selectedStore === 'all' && (
                                <div className="tab-group pc-only" style={{ display: 'flex', backgroundColor: 'rgba(255, 255, 255, 0.05)', padding: '4px', borderRadius: '8px', border: '1px solid rgba(56, 189, 248, 0.2)' }}>
                                    <button
                                        className={`tab-button ${filterType === 'all' ? 'active' : ''}`}
                                        onClick={() => setFilterType('all')}
                                        style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', background: filterType === 'all' ? '#38bdf8' : 'transparent', color: filterType === 'all' ? '#fff' : '#94a3b8', fontSize: '13px' }}
                                    >전체</button>
                                    <button
                                        className={`tab-button ${filterType === 'direct' ? 'active' : ''}`}
                                        onClick={() => setFilterType('direct')}
                                        style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', background: filterType === 'direct' ? '#38bdf8' : 'transparent', color: filterType === 'direct' ? '#fff' : '#94a3b8', fontSize: '13px' }}
                                    >직영</button>
                                    <button
                                        className={`tab-button ${filterType === 'rental' ? 'active' : ''}`}
                                        onClick={() => setFilterType('rental')}
                                        style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', background: filterType === 'rental' ? '#38bdf8' : 'transparent', color: filterType === 'rental' ? '#fff' : '#94a3b8', fontSize: '13px' }}
                                    >임대</button>
                                </div>
                            )}
                        </div>

                        <div className="filter-controls" style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                            <div className="store-selector" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Filter size={18} color="#94a3b8" />
                                <select
                                    value={selectedStore}
                                    onChange={(e) => setSelectedStore(e.target.value)}
                                    style={{ padding: '8px', borderRadius: '6px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#fff' }}
                                >
                                    <option value="all">전체 매장 통합</option>
                                    {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>

                            {viewType !== 'yearly' && (
                                <div className="date-selector" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '13px', color: '#94a3b8', marginRight: '4px' }}>조회 날짜:</span>
                                    <Calendar size={18} color="#94a3b8" />
                                    <select
                                        value={selectedYear}
                                        onChange={(e) => {
                                            const newYear = e.target.value;
                                            setSelectedYear(newYear);
                                            // URL 파라미터 업데이트
                                            const queryParams = new URLSearchParams(location.search);
                                            queryParams.set('date', `${newYear}-${selectedMonth || '01'}-01`);
                                            navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
                                        }}
                                        style={{ padding: '8px', borderRadius: '6px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#fff' }}
                                    >
                                        {years.map(y => <option key={y} value={y}>{y}년</option>)}
                                    </select>

                                    {viewType === 'daily' && (
                                        <select
                                            value={selectedMonth}
                                            onChange={(e) => {
                                                const newMonth = e.target.value;
                                                setSelectedMonth(newMonth);
                                                // URL 파라미터 업데이트
                                                const queryParams = new URLSearchParams(location.search);
                                                queryParams.set('date', `${selectedYear}-${newMonth}-01`);
                                                navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
                                            }}
                                            style={{ padding: '8px', borderRadius: '6px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#fff' }}
                                        >
                                            {months.map(m => <option key={m} value={m}>{Number(m)}월</option>)}
                                        </select>
                                    )}
                                </div>
                            )}

                            {selectedStore !== 'all' && (
                                <div style={{ minWidth: '100px' }}></div> // 레이아웃 유지를 위한 빈 공간 또는 다른 요소
                            )}
                        </div>
                    </div>
                </div>

                <div className="glass-card" style={{ padding: '32px', minHeight: '100px', marginBottom: '24px' }}>
                    {/* [v2.8] "New Screen" - Numerical Analytics & Top 3 Stores are primary. 
                        The old bar chart was removed here, focusing on the data below. */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            className="icon-button"
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#38bdf8' }}
                            onClick={() => {
                                if (chartData.length === 0) return showAlert('내보낼 데이터가 없습니다.');

                                // CSV 헤더 및 내용 구성
                                const headers = ['구분', '매출액(₩)'];
                                const rows = chartData.map(d => [d.label, d.value]);

                                const csvContent = [
                                    headers.join(','),
                                    ...rows.map(r => r.join(','))
                                ].join('\n');

                                // 한글 깨짐 방지를 위한 BOM 추가
                                const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');

                                const storeName = selectedStore === 'all' ? '전체매장' : stores.find(s => s.id === selectedStore)?.name;
                                const typeLabel = viewType === 'daily' ? `${Number(selectedMonth)}월_일별` : viewType === 'monthly' ? '월별' : viewType === 'quarterly' ? '분기별' : '연별';
                                const fileName = `${selectedYear}년_${storeName}_${typeLabel}_매출통계.csv`;

                                link.setAttribute('href', url);
                                link.setAttribute('download', fileName);
                                link.style.visibility = 'hidden';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }}
                        >
                            <Download size={16} /> 데이터 내보내기 (CSV)
                        </button>
                    </div>
                </div>

                {/* 매출 요약 지표 */}
                <div className="stats-grid" style={{ marginBottom: '24px' }}>
                    <div className="stat-card">
                        <span className="stat-label">조회 기간 총 매출 ({filterLabel})</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span className="stat-value" style={{ fontSize: '1.8rem' }}>₩ {totalSum.toLocaleString()}</span>
                            {totalGrowth !== 0 && (
                                <span style={{ 
                                    fontSize: '0.9rem', 
                                    padding: '2px 6px', 
                                    borderRadius: '4px', 
                                    backgroundColor: totalGrowth > 0 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                    color: totalGrowth > 0 ? '#22c55e' : '#ef4444',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    {totalGrowth === '신규' ? '신규' : (
                                        <>
                                            {totalGrowth.startsWith('-') ? '▼' : '▲'} {totalGrowth.replace('-', '')}
                                        </>
                                    )}
                                </span>
                            )}
                        </div>
                        <span className="stat-trend" style={{ color: '#94a3b8' }}>
                            {viewType === 'daily' ? '전월 동기간 대비' : viewType === 'monthly' ? '전년 대비' : '이전 기간 대비'}
                        </span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">항목별 평균 매출</span>
                        <span className="stat-value">₩ {Math.floor(averageSales).toLocaleString()}</span>
                        <span className="stat-trend">데이터 평균치</span>
                    </div>
                    
                    {/* 추천 기능: TOP 3 매장 (전체 매장 조회 시에만 유용) */}
                    {selectedStore === 'all' && topStores.length > 0 && (
                        <div className="stat-card" style={{ gridColumn: 'span 2', background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.1) 0%, rgba(56, 189, 248, 0.05) 100%)', border: '1px solid rgba(56, 189, 248, 0.2)' }}>
                            <span className="stat-label" style={{ color: '#38bdf8', fontWeight: 'bold' }}>⭐ {filterLabel} 매장 조회 기간 TOP 3 우수 매장</span>
                            <div style={{ display: 'flex', gap: '20px', marginTop: '12px' }}>
                                {topStores.map((s, idx) => (
                                    <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <span style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{idx + 1}위</span>
                                        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#f8fafc', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                                        <span style={{ fontSize: '13px', color: '#38bdf8' }}>₩ {(s.total / 10000).toFixed(0)}만</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* 상세 매출 수치 테이블 */}
                <div className="glass-card" style={{ padding: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <BarChart size={20} color="#38bdf8" />
                            <h3 style={{ margin: 0 }}>매출 성장 분석 상세 테이블</h3>
                        </div>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>* 증감율은 이전 기간 동시점 대비 수치입니다.</span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="management-table">
                            <thead>
                                <tr>
                                    <th>구분 (기간/항목)</th>
                                    <th style={{ textAlign: 'right' }}>매출액</th>
                                    <th style={{ textAlign: 'right' }}>이전 대비 증감</th>
                                    <th style={{ textAlign: 'right' }}>비중</th>
                                </tr>
                            </thead>
                            <tbody>
                                {chartData.length > 0 ? (
                                    chartData.map((data, idx) => (
                                        <tr 
                                            key={idx} 
                                            onClick={() => {
                                                if (viewType === 'daily') {
                                                    const day = data.label.replace('일', '').padStart(2, '0');
                                                    const dateStr = `${selectedYear}-${selectedMonth}-${day}`;
                                                    if (selectedStore !== 'all') {
                                                        navigate(`/store/${selectedStore}/sales?date=${dateStr}`);
                                                    } else {
                                                        navigate(`/dashboard?date=${dateStr}`);
                                                    }
                                                } else if (viewType === 'monthly') {
                                                    setViewType('daily');
                                                    setSelectedMonth(data.label.replace('월', '').padStart(2, '0'));
                                                    // URL 파라미터 업데이트
                                                    const queryParams = new URLSearchParams(location.search);
                                                    queryParams.set('date', `${selectedYear}-${data.label.replace('월', '').padStart(2, '0')}-01`);
                                                    navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
                                                }
                                            }}
                                            style={{ 
                                                background: data.value === maxValue ? 'rgba(56, 189, 248, 0.03)' : 'transparent',
                                                cursor: 'pointer',
                                                transition: 'background 0.2s ease'
                                            }}
                                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(56, 189, 248, 0.1)'}
                                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = data.value === maxValue ? 'rgba(56, 189, 248, 0.03)' : 'transparent'}
                                            title="클릭 시 상세 보기"
                                        >
                                            <td className="item-name">
                                                {data.label}
                                                {data.isNew && <span style={{ marginLeft: '8px', fontSize: '10px', padding: '2px 6px', background: '#22c55e', color: '#fff', borderRadius: '10px' }}>신규 매출</span>}
                                                {data.value === maxValue && <span style={{ marginLeft: '8px', fontSize: '10px', padding: '2px 6px', background: '#38bdf8', color: '#fff', borderRadius: '10px' }}>최고 매출</span>}
                                            </td>
                                            <td style={{ textAlign: 'right', fontWeight: '700', color: '#38bdf8' }}>
                                                ₩ {data.value.toLocaleString()}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {(data.isNew || data.growth !== 0) ? (
                                                    <span style={{ 
                                                        color: data.isNew || data.growth > 0 ? '#22c55e' : '#ef4444', 
                                                        fontSize: '13px',
                                                        fontWeight: '600'
                                                    }}>
                                                        {data.isNew ? '' : (data.growth > 0 ? '▲' : '▼')} {
                                                            data.isNew ? '신규' : (
                                                                data.growth > 999 ? '999%+' : 
                                                                (data.growth < -999 ? '999%+' : `${Math.abs(data.growth).toFixed(1)}%`)
                                                            )
                                                        }
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#64748b', fontSize: '13px' }}>-</span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'right', color: '#94a3b8', fontSize: '13px' }}>
                                                {totalSum > 0 ? `${((data.value / totalSum) * 100).toFixed(1)}%` : '0%'}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                            데이터가 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            {chartData.length > 0 && (
                                <tfoot>
                                    <tr style={{ background: 'rgba(56, 189, 248, 0.05)' }}>
                                        <td style={{ fontWeight: '800', color: '#f8fafc' }}>합계</td>
                                        <td style={{ textAlign: 'right', fontWeight: '800', color: '#f8fafc', fontSize: '1.1rem' }}>
                                            ₩ {totalSum.toLocaleString()}
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: '800', color: totalGrowth === '신규' || !totalGrowth.startsWith('-') ? '#22c55e' : '#ef4444' }}>
                                            {totalSum > 0 ? (totalGrowth === '신규' ? '신규' : (
                                                <>
                                                    {totalGrowth.startsWith('-') ? '▼' : '▲'} {totalGrowth.replace('-', '')}
                                                </>
                                            )) : '-'}
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: '800', color: '#f8fafc' }}>100%</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>

                {/* 백업 관리 섹션 */}
                <div className="glass-card" style={{ padding: '24px', marginTop: '40px', borderTop: '2px solid rgba(34, 197, 94, 0.3)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <Package size={24} color="#22c55e" />
                        <h3 style={{ margin: 0, color: '#22c55e' }}>백업 관리</h3>
                    </div>
                    <p style={{ color: '#94a3b8', fontSize: '15px', marginBottom: '24px', lineHeight: '1.6' }}>
                        📦 데이터 삭제 시 자동으로 백업이 생성됩니다. 백업은 최근 **30개**까지 보관됩니다.<br />
                        🕐 정기 백업: 매주 월요일 12시에 자동으로 백업이 생성됩니다.
                    </p>

                    {backups && backups.length > 0 ? (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="management-table">
                                <thead>
                                    <tr className="header-item">
                                        <th style={{ textAlign: 'left', padding: '16px', color: '#94a3b8', minWidth: '160px' }}>백업 시간</th>
                                        <th style={{ textAlign: 'center', padding: '16px', color: '#94a3b8', minWidth: '110px' }}>백업 타입</th>
                                        <th style={{ textAlign: 'center', padding: '16px', color: '#94a3b8', minWidth: '120px' }}>데이터 크기</th>
                                        <th style={{ textAlign: 'center', padding: '16px', color: '#94a3b8', width: '100px' }}>복구</th>
                                        <th style={{ textAlign: 'center', padding: '16px', color: '#94a3b8', width: '100px' }}>삭제</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {backups.map((backup, idx) => {
                                        // 서버 백업(backup.time)과 로컬 생성 백업(backup.createdAt) 모두 지원
                                        const backupDateStr = backup.time || backup.createdAt || '';
                                        const backupDate = backupDateStr ? new Date(backupDateStr) : new Date();
                                        
                                        // 날짜 포맷 단축 (YY.MM.DD HH:mm)
                                        const dateStr = backupDateStr 
                                            ? `${backupDate.getFullYear().toString().slice(2)}.${(backupDate.getMonth() + 1).toString().padStart(2, '0')}.${backupDate.getDate().toString().padStart(2, '0')} ${backupDate.getHours().toString().padStart(2, '0')}:${backupDate.getMinutes().toString().padStart(2, '0')}`
                                            : '알 수 없음';
                                        
                                        // 파일 기반 백업의 경우 이름(name)에서 파생
                                        let backupType = backup?.type || '';
                                        if (!backupType && backup.name) {
                                            if (backup.name.includes('scheduled_weekly')) backupType = 'scheduled_weekly';
                                            else backupType = '서버 백업';
                                        }
                                        let typeLabel = backupType;

                                        if (backupType === 'scheduled_weekly') {
                                            typeLabel = '정기 백업';
                                        } else if (backupType === 'manual') {
                                            typeLabel = '수동 백업';
                                        } else if (backupType) {
                                            // 자동 백업 레이블 한글화
                                            const typeMap = {
                                                'vendor': '거래처 백업',
                                                'sales': '매출 백업',
                                                'inventory': '재고 백업',
                                                'facility': '시설 백업',
                                                'order': '발주 백업',
                                                'all': '전체 데이터 백업'
                                            };
                                            
                                            const rawType = backupType.replace('auto_before_', '').replace('_clear', '');
                                            typeLabel = typeMap[rawType] || rawType.replace('_', ' ');
                                        }

                                        // Safety check: 서버에서 받은 size가 있으면 먼저 사용하고, 없으면 data를 텍스트화하여 계산
                                        const dataSize = backup?.size !== undefined ? backup.size : (backup && backup.data ? JSON.stringify(backup.data).length : 0);

                                        return (
                                            <tr key={backup.id || backup.name || idx}>
                                                <td style={{ textAlign: 'left', padding: '16px', fontSize: '13px', letterSpacing: '-0.5px' }}>{dateStr}</td>
                                                <td style={{ textAlign: 'center', padding: '16px', whiteSpace: 'nowrap' }}>
                                                    <span style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        background: backup.type === 'scheduled_weekly' ? 'rgba(34, 197, 94, 0.1)' :
                                                            backup.type?.startsWith('auto') ? 'rgba(251, 191, 36, 0.1)' : 'rgba(56, 189, 248, 0.1)',
                                                        color: backup.type === 'scheduled_weekly' ? '#22c55e' :
                                                            backup.type?.startsWith('auto') ? '#fbbf24' : '#38bdf8'
                                                    }}>
                                                        {typeLabel}
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: 'center', padding: '16px' }}>{(dataSize / 1024).toFixed(1)} KB</td>
                                                <td style={{ textAlign: 'center', padding: '16px' }}>
                                                    <button
                                                        className="icon-button"
                                                        onClick={async () => {
                                                            showAlert('⚠️ 경고: 이 백업으로 복구하시겠습니까? 현재 데이터는 덮어씌워집니다.', {
                                                                type: 'confirm',
                                                                onConfirm: () => {
                                                                    showAlert('복구하면 현재 데이터가 사라집니다. 정말로 계속하시겠습니까?', {
                                                                        type: 'confirm',
                                                                        onConfirm: () => {
                                                                            showAlert('보안을 위해 상위 비밀번호(Master Key)를 입력해주세요.', {
                                                                                type: 'prompt',
                                                                                onConfirm: async (password) => {
                                                                                    if (password !== 'kkh1234') {
                                                                                        showAlert('상위 비밀번호가 일치하지 않습니다. 권한이 없습니다.');
                                                                                        return;
                                                                                    }
                                                                                    try {
                                                                                        await restoreFromBackup(backup.id);
                                                                                        showAlert('✅ 백업이 성공적으로 복구되었습니다.');
                                                                                        window.location.reload();
                                                                                    } catch (error) {
                                                                                        showAlert('❌ 복구 실패: ' + error.message);
                                                                                    }
                                                                                }
                                                                            });
                                                                        }
                                                                    });
                                                                }
                                                            });
                                                        }}
                                                        style={{
                                                            color: '#22c55e',
                                                            background: 'rgba(34, 197, 94, 0.1)',
                                                            border: '1px solid #22c55e',
                                                            padding: '6px 12px',
                                                            borderRadius: '6px',
                                                            fontSize: '13px'
                                                        }}
                                                    >
                                                        복구
                                                    </button>
                                                </td>
                                                <td style={{ textAlign: 'center', padding: '16px' }}>
                                                    <button
                                                        className="icon-button"
                                                        onClick={() => {
                                                            showAlert('이 백업을 삭제하시겠습니까?', {
                                                                type: 'confirm',
                                                                onConfirm: () => {
                                                                    showAlert('보안을 위해 상위 비밀번호(Master Key)를 입력해주세요.', {
                                                                        type: 'prompt',
                                                                        onConfirm: (password) => {
                                                                            if (password !== 'kkh1234') {
                                                                                showAlert('상위 비밀번호가 일치하지 않습니다. 권한이 없습니다.');
                                                                                return;
                                                                            }
                                                                            deleteBackup(backup.id);
                                                                        }
                                                                    });
                                                                }
                                                            });
                                                        }}
                                                        style={{
                                                            color: '#ef4444',
                                                            background: 'rgba(239, 68, 68, 0.1)',
                                                            border: '1px solid #ef4444',
                                                            padding: '6px 12px',
                                                            borderRadius: '6px',
                                                            fontSize: '13px'
                                                        }}
                                                    >
                                                        삭제
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                            생성된 백업이 없습니다.
                        </div>
                    )}

                    {/* 수동 백업 생성 */}
                    <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <button
                            className="full-width-button"
                            onClick={async () => {
                                try {
                                    await createBackup('manual');
                                    showAlert('✅ 수동 백업이 생성되었습니다.');
                                } catch (error) {
                                    showAlert('❌ 백업 생성 실패: ' + error.message);
                                }
                            }}
                            style={{
                                background: 'rgba(34, 197, 94, 0.15)',
                                border: '1px solid #22c55e',
                                color: '#22c55e'
                            }}
                        >
                            <Package size={18} style={{ marginRight: '8px' }} /> 현재 데이터 백업 생성
                        </button>
                    </div>

                    {/* 서버 보관 백업 목록 (자동/정기 백업 확인용) */}
                    <div style={{ marginTop: '32px', padding: '20px', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <Database size={18} color="#94a3b8" />
                            <h4 style={{ margin: 0, color: '#94a3b8', fontSize: '0.95rem' }}>서버 보관 백업 (정기/자동 백업 포함)</h4>
                        </div>
                        {serverBackups.length > 0 ? (() => {
                            const scheduledBackups = serverBackups.filter(f => f.name.includes('scheduled')).slice(0, 15);
                            const autoBackups = serverBackups.filter(f => !f.name.includes('scheduled')).slice(0, 15);
                            const displayBackups = [...scheduledBackups, ...autoBackups];
                            return (
                                <div>
                                    <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', fontSize: '12px' }}>
                                        <span style={{ color: '#22c55e' }}>📅 정기 백업: {scheduledBackups.length}개</span>
                                        <span style={{ color: '#94a3b8' }}>📄 자동/기타: {autoBackups.length}개</span>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                                        {displayBackups.map((f, i) => (
                                            <div key={i} style={{ fontSize: '12px', padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                <div style={{ color: f.name.includes('scheduled') ? '#22c55e' : '#94a3b8', fontWeight: 'bold', marginBottom: '4px' }}>
                                                    {f.name.includes('scheduled') ? '📅 정기 백업' : '📄 자동/기타'}
                                                </div>
                                                <div style={{ color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.name}>{f.name}</div>
                                                <div style={{ color: '#64748b', marginTop: '4px' }}>{f && f.size ? (f.size / 1024).toFixed(1) : 0} KB</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })() : (
                            <div style={{ fontSize: '13px', color: '#64748b', textAlign: 'center', padding: '10px' }}>보관된 서버 백업이 없습니다.</div>
                        )}
                        <p style={{ marginTop: '16px', fontSize: '11px', color: '#64748b', lineHeight: '1.4' }}>
                            * 이 목록은 서버의 `backups/` 폴더 내 실제 파일들을 보여줍니다. 브라우저가 꺼져 있을 때 생성된 정기 백업도 여기서 확인 가능합니다.
                        </p>
                    </div>
                </div>

                {/* 데이터 관리 섹션 */}
                <div className="glass-card" style={{ padding: '32px', marginTop: '40px', borderTop: '2px solid rgba(239, 68, 68, 0.3)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <Database size={24} color="#ef4444" />
                        <h3 style={{ margin: 0, color: '#ef4444' }}>데이터 관리</h3>
                    </div>
                    <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '32px' }}>
                        ⚠️ 위험: 아래 기능은 데이터를 영구적으로 삭제합니다. 실행 전 반드시 확인하세요.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px' }}>
                        {/* 거래처 데이터 초기화 */}
                        <button
                            className="full-width-button"
                            onClick={async () => {
                                                            showAlert('⚠️ 경고: 모든 거래처 데이터를 삭제하시겠습니까?', {
                                                                type: 'confirm',
                                                                onConfirm: () => {
                                                                    showAlert('이 작업은 되돌릴 수 없습니다. 정말로 계속하시겠습니까?', {
                                                                        type: 'confirm',
                                                                        onConfirm: () => {
                                                                            showAlert('보안을 위해 상위 비밀번호(Master Key)를 입력해주세요.', {
                                                                                type: 'prompt',
                                                                                onConfirm: async (password) => {
                                                                                    if (password !== 'kkh1234') {
                                                                                        showAlert('상위 비밀번호가 일치하지 않습니다. 권한이 없습니다.');
                                                                                        return;
                                                                                    }
                                                                                    await clearVendorsData();
                                                                                    showAlert('✅ 거래처 데이터가 초기화되었습니다.');
                                                                                    window.location.reload();
                                                                                }
                                                                            });
                                                                        }
                                                                    });
                                                                }
                                                            });
                            }}
                            style={{
                                background: 'rgba(251, 191, 36, 0.1)',
                                border: '1px solid #fbbf24',
                                color: '#fbbf24'
                            }}
                        >
                            <Trash2 size={18} style={{ marginRight: '8px' }} /> 거래처 데이터 초기화
                        </button>

                        {/* 매출 데이터 초기화 */}
                        <button
                            className="full-width-button"
                            onClick={async () => {
                                                            showAlert('⚠️ 경고: 모든 매장의 매출 데이터를 삭제하시겠습니까?', {
                                                                type: 'confirm',
                                                                onConfirm: () => {
                                                                    showAlert('이 작업은 되돌릴 수 없습니다. 정말로 계속하시겠습니까?', {
                                                                        type: 'confirm',
                                                                        onConfirm: () => {
                                                                            showAlert('보안을 위해 상위 비밀번호(Master Key)를 입력해주세요.', {
                                                                                type: 'prompt',
                                                                                onConfirm: async (password) => {
                                                                                    if (password !== 'kkh1234') {
                                                                                        showAlert('상위 비밀번호가 일치하지 않습니다. 권한이 없습니다.');
                                                                                        return;
                                                                                    }
                                                                                    await clearSalesData();
                                                                                    showAlert('✅ 매출 데이터가 초기화되었습니다.');
                                                                                    window.location.reload();
                                                                                }
                                                                            });
                                                                        }
                                                                    });
                                                                }
                                                            });
                            }}
                            style={{
                                background: 'rgba(251, 191, 36, 0.1)',
                                border: '1px solid #fbbf24',
                                color: '#fbbf24'
                            }}
                        >
                            <Trash2 size={18} style={{ marginRight: '8px' }} /> 매출 데이터 초기화
                        </button>

                        {/* 재고 데이터 초기화 */}
                        <button
                            className="full-width-button"
                            onClick={async () => {
                                                            showAlert('⚠️ 경고: 모든 매장의 재고 데이터를 삭제하시겠습니까?', {
                                                                type: 'confirm',
                                                                onConfirm: () => {
                                                                    showAlert('이 작업은 되돌릴 수 없습니다. 정말로 계속하시겠습니까?', {
                                                                        type: 'confirm',
                                                                        onConfirm: () => {
                                                                            showAlert('보안을 위해 상위 비밀번호(Master Key)를 입력해주세요.', {
                                                                                type: 'prompt',
                                                                                onConfirm: async (password) => {
                                                                                    if (password !== 'kkh1234') {
                                                                                        showAlert('상위 비밀번호가 일치하지 않습니다. 권한이 없습니다.');
                                                                                        return;
                                                                                    }
                                                                                    await clearInventoryData();
                                                                                    showAlert('✅ 재고 데이터가 초기화되었습니다.');
                                                                                    window.location.reload();
                                                                                }
                                                                            });
                                                                        }
                                                                    });
                                                                }
                                                            });
                            }}
                            style={{
                                background: 'rgba(251, 191, 36, 0.1)',
                                border: '1px solid #fbbf24',
                                color: '#fbbf24'
                            }}
                        >
                            <Trash2 size={18} style={{ marginRight: '8px' }} /> 재고 데이터 초기화
                        </button>

                        {/* 시설 데이터 초기화 */}
                        <button
                            className="full-width-button"
                            onClick={async () => {
                                                            showAlert('⚠️ 경고: 모든 매장의 시설 데이터를 삭제하시겠습니까?', {
                                                                type: 'confirm',
                                                                onConfirm: () => {
                                                                    showAlert('이 작업은 되돌릴 수 없습니다. 정말로 계속하시겠습니까?', {
                                                                        type: 'confirm',
                                                                        onConfirm: () => {
                                                                            showAlert('보안을 위해 상위 비밀번호(Master Key)를 입력해주세요.', {
                                                                                type: 'prompt',
                                                                                onConfirm: async (password) => {
                                                                                    if (password !== 'kkh1234') {
                                                                                        showAlert('상위 비밀번호가 일치하지 않습니다. 권한이 없습니다.');
                                                                                        return;
                                                                                    }
                                                                                    await clearFacilityData();
                                                                                    showAlert('✅ 시설 데이터가 초기화되었습니다.');
                                                                                    window.location.reload();
                                                                                }
                                                                            });
                                                                        }
                                                                    });
                                                                }
                                                            });
                            }}
                            style={{
                                background: 'rgba(251, 191, 36, 0.1)',
                                border: '1px solid #fbbf24',
                                color: '#fbbf24'
                            }}
                        >
                            <Trash2 size={18} style={{ marginRight: '8px' }} /> 시설 데이터 초기화
                        </button>

                        {/* 발주 데이터 초기화 */}
                        <button
                            className="full-width-button"
                            onClick={async () => {
                                                            showAlert('⚠️ 경고: 모든 매장의 발주 데이터를 삭제하시겠습니까?', {
                                                                type: 'confirm',
                                                                onConfirm: () => {
                                                                    showAlert('이 작업은 되돌릴 수 없습니다. 정말로 계속하시겠습니까?', {
                                                                        type: 'confirm',
                                                                        onConfirm: () => {
                                                                            showAlert('보안을 위해 상위 비밀번호(Master Key)를 입력해주세요.', {
                                                                                type: 'prompt',
                                                                                onConfirm: async (password) => {
                                                                                    if (password !== 'kkh1234') {
                                                                                        showAlert('상위 비밀번호가 일치하지 않습니다. 권한이 없습니다.');
                                                                                        return;
                                                                                    }
                                                                                    await clearOrderData();
                                                                                    showAlert('✅ 발주 데이터가 초기화되었습니다.');
                                                                                    window.location.reload();
                                                                                }
                                                                            });
                                                                        }
                                                                    });
                                                                }
                                                            });
                            }}
                            style={{
                                background: 'rgba(251, 191, 36, 0.1)',
                                border: '1px solid #fbbf24',
                                color: '#fbbf24'
                            }}
                        >
                            <Trash2 size={18} style={{ marginRight: '8px' }} /> 발주 데이터 초기화
                        </button>
                    </div>



                    {/* 전체 데이터 일괄 초기화 */}
                    <div style={{ paddingTop: '24px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <AlertTriangle size={20} color="#ef4444" />
                            <h4 style={{ margin: 0, color: '#ef4444' }}>전체 데이터 일괄 초기화</h4>
                        </div>
                        <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '16px' }}>
                            거래처, 매출, 재고, 시설, 발주 데이터를 모두 삭제합니다.
                        </p>
                        <button
                            className="full-width-button"
                            onClick={async () => {
                                                            showAlert('🚨 최종 경고: 모든 입력 데이터(거래처/매출/재고/시설/발주)를 삭제하시겠습니까?', {
                                                                type: 'confirm',
                                                                onConfirm: () => {
                                                                    showAlert('이 작업은 절대 되돌릴 수 없습니다. 정말로 모든 데이터를 삭제하시겠습니까?', {
                                                                        type: 'confirm',
                                                                        onConfirm: () => {
                                                                            showAlert('보안을 위해 상위 비밀번호(Master Key)를 입력해주세요.', {
                                                                                type: 'prompt',
                                                                                onConfirm: async (password) => {
                                                                                    if (password !== 'kkh1234') {
                                                                                        showAlert('상위 비밀번호가 일치하지 않습니다. 권한이 없습니다.');
                                                                                        return;
                                                                                    }
                                                                                    await clearAllData();
                                                                                    showAlert('✅ 모든 데이터가 초기화되었습니다.');
                                                                                    window.location.reload();
                                                                                }
                                                                            });
                                                                        }
                                                                    });
                                                                }
                                                            });
                            }}
                            style={{
                                background: 'rgba(239, 68, 68, 0.15)',
                                border: '2px solid #ef4444',
                                color: '#ef4444',
                                fontWeight: '700'
                            }}
                        >
                            <AlertTriangle size={18} style={{ marginRight: '8px' }} /> 전체 데이터 일괄 초기화
                        </button>
                    </div>
                </div>

                {/* 어드민 계정 관리 섹션 - 마스터 계정(admin)만 볼 수 있음 */}
                {user?.id === 'admin' && (
                    <>
                        <div className="glass-card management-card" style={{ marginTop: '32px', border: '1px solid rgba(56, 189, 248, 0.3)', padding: '32px' }}>
                            <div className="settings-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                                <SettingsIcon size={24} color="#38bdf8" />
                                <h3 style={{ margin: 0 }}>어드민 계정 관리 (이중 보안 모드)</h3>
                            </div>

                            <div className="settings-form" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px', marginTop: '20px' }}>
                                {admins.map((admin) => (
                                    <div key={admin.id} className="input-field" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <label style={{ color: '#94a3b8', fontSize: '14px' }}>{admin.name} ({admin.id}) 비밀번호</label>
                                        <input
                                            type="password"
                                            defaultValue={admin.password}
                                            id={`admin-pw-${admin.id}`}
                                            placeholder="비밀번호 입력"
                                            style={{ padding: '12px', borderRadius: '8px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#fff' }}
                                        />
                                    </div>
                                ))}
                            </div>

                            <button 
                                className="full-width-button" 
                                style={{ 
                                    marginTop: '32px', 
                                    background: 'rgba(56, 189, 248, 0.2)', 
                                    border: '1px solid #38bdf8', 
                                    color: '#38bdf8',
                                    padding: '16px',
                                    borderRadius: '8px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    width: '100%'
                                }}
                                onClick={() => {
                                    const masterKey = window.prompt('보안을 위해 상위 비밀번호(Master Key)를 입력해주세요.');
                                    if (masterKey === 'kkh1234') {
                                        const updatedAdmins = admins.map(admin => ({
                                            ...admin,
                                            password: document.getElementById(`admin-pw-${admin.id}`).value
                                        }));
                                        
                                        fetch('/api/admins', {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify(updatedAdmins)
                                        }).then(res => res.json()).then(data => {
                                            if (data.success) {
                                                showAlert('어드민 계정 정보가 성공적으로 업데이트되었습니다. 다시 로그인 시 적용됩니다.');
                                                window.location.reload();
                                            }
                                        });
                                    } else if (masterKey !== null) {
                                        showAlert('상위 비밀번호가 일치하지 않습니다. 권한이 없습니다.');
                                    }
                                }}
                            >
                                어드민 계정 정보 저장 (이중 인증)
                            </button>
                        </div>

                        {/* 매니저 계정 관리 섹션 */}
                        <div className="glass-card management-card" style={{ marginTop: '32px', border: '1px solid rgba(168, 85, 247, 0.3)', padding: '32px' }}>
                            <div className="settings-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                                <SettingsIcon size={24} color="#a855f7" />
                                <h3 style={{ margin: 0 }}>매니저 계정 관리 (store1 ~ store50)</h3>
                            </div>

                            <div style={{ marginBottom: '24px', padding: '16px', background: 'rgba(168, 85, 247, 0.05)', borderRadius: '8px', border: '1px solid rgba(168, 85, 247, 0.1)' }}>
                                <label style={{ color: '#94a3b8', fontSize: '14px', display: 'block', marginBottom: '8px' }}>매니저 비밀번호 일괄 초기화 (전체 매장)</label>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <input
                                        type="text"
                                        id="bulk-manager-pw"
                                        placeholder="초기화할 공통 비밀번호 입력"
                                        style={{ flex: 1, padding: '12px', borderRadius: '8px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#fff' }}
                                    />
                                    <button
                                        onClick={() => {
                                            const newPw = document.getElementById('bulk-manager-pw').value;
                                            if (!newPw) return showAlert('초기화할 비밀번호를 입력해주세요.');
                                            
                                            const masterKey = window.prompt(`전체 매장의 비밀번호를 [${newPw}]로 일괄 변경하시겠습니까?\n보안을 위해 상위 비밀번호(Master Key)를 입력해주세요.`);
                                            if (masterKey === 'kkh1234') {
                                                const { updateAllStores } = useStore.getState(); // StoreContext의 updateAllStores 호출
                                                // 컴포넌트 내부에서 useStore를 이미 사용 중이므로 직접 처리가 더 안전함
                                                const updatedStores = stores.map(s => ({ ...s, password: newPw }));
                                                
                                                fetch('/api/stores', {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify(updatedStores)
                                                }).then(res => res.ok ? res.json() : null).then(data => {
                                                    showAlert(`전체 매장의 비밀번호가 [${newPw}]로 일괄 변경되었습니다.`);
                                                    window.location.reload();
                                                });
                                            } else if (masterKey !== null) {
                                                showAlert('상위 비밀번호가 일치하지 않습니다.');
                                            }
                                        }}
                                        style={{ padding: '0 24px', background: '#a855f7', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                                    >
                                        일괄 변경 실행
                                    </button>
                                </div>
                            </div>

                            <div className="settings-form" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', maxHeight: '400px', overflowY: 'auto', paddingRight: '8px' }}>
                                {stores.sort((a, b) => Number(a.id) - Number(b.id)).map((store) => (
                                    <div key={store.id} style={{ padding: '12px', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                        <label style={{ color: '#94a3b8', fontSize: '12px', display: 'block', marginBottom: '4px' }}>{store.name} ({store.managerId})</label>
                                        <input
                                            type="text"
                                            defaultValue={store.password}
                                            id={`store-pw-${store.id}`}
                                            style={{ width: '100%', padding: '8px', borderRadius: '4px', backgroundColor: '#0f172a', border: '1px solid #334155', color: '#fff', fontSize: '13px' }}
                                        />
                                    </div>
                                ))}
                            </div>

                            <button 
                                className="full-width-button" 
                                style={{ 
                                    marginTop: '24px', 
                                    background: 'rgba(168, 85, 247, 0.2)', 
                                    border: '1px solid #a855f7', 
                                    color: '#a855f7',
                                    padding: '16px',
                                    borderRadius: '8px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    width: '100%'
                                }}
                                onClick={() => {
                                    const masterKey = window.prompt('변경된 개별 매장 비밀번호를 저장하시겠습니까?\n보안을 위해 상위 비밀번호(Master Key)를 입력해주세요.');
                                    if (masterKey === 'kkh1234') {
                                        const updatedStores = stores.map(store => ({
                                            ...store,
                                            password: document.getElementById(`store-pw-${store.id}`).value
                                        }));
                                        
                                        fetch('/api/stores', {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify(updatedStores)
                                        }).then(res => res.ok ? res.json() : null).then(data => {
                                            showAlert('매장 비밀번호 정보가 성공적으로 업데이트되었습니다.');
                                            window.location.reload();
                                        });
                                    } else if (masterKey !== null) {
                                        showAlert('상위 비밀번호가 일치하지 않습니다.');
                                    }
                                }}
                            >
                                매니저 계정 정보 일괄 저장 (이중 인증)
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
};

export default Analytics;
